import {
    Injectable, inject
} from "@angular/core";
import {
    FileRequestSubject,
    FileResponse,
    RequestUtil
} from "@shopliftr/common-js/shared";
import { MimeType } from "@shopliftr/common-js/data";
import {
    Observable
} from "rxjs";

import { IInternalApiFile } from "../../../../interfaces/internal-api-file";
import { InternalApiFileRequestService } from "../../request/internal-api/internal-api-file-request.service";

/**
 * API File Service for StoreImportSpreadsheet file operations.
 *
 * Handles file management operations for store-import-spreadsheet files,
 * delegating HTTP communication to InternalApiFileRequestService.
 *
 * @pattern API File Service / StoreImportSpreadsheet File Management
 * @providedIn root
 * @implements {IInternalApiFile}
 */
@Injectable({ providedIn: "root" })
export class StoreImportSpreadsheetApiFileService implements IInternalApiFile {

    readonly fileRequestSubject = FileRequestSubject.StoreImportSpreadsheet;

    private readonly _internalApiFileRequestService = inject(InternalApiFileRequestService);

    /**
     * Uploads a Deals API Bulk Store spreadsheet (.xlsx) to the stores feed bucket.
     *
     * The server rewrites the key to `<basename>-<epochMillis>.xlsx`, so callers pass the plain file name.
     * The stored key is returned in `results[0].filePath` of the response.
     * Some browsers report an empty `type` for .xlsx files (for example Chromium on Windows without
     * Office installed), so the content type falls back to `MimeType.MsExcelXml`.
     *
     * @param filePath - Plain file name of the spreadsheet (the server makes the key unique)
     * @param file - File blob to upload
     * @returns Observable that emits the file upload response
     */
    upload$(filePath: string, file: Blob): Observable<FileResponse> {

        const contentType = (file.type as MimeType) || MimeType.MsExcelXml;
        const request = RequestUtil.createFileUploadRequest(this.fileRequestSubject, filePath, contentType);
        return this._internalApiFileRequestService.uploadFile$(request, file);
    }
}
